import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AudioPlayer from "./components/AudioPlayer";
import ContactForm from "./components/ContactForm";
import Videos from "./components/Videos";
import Music from "./components/Music";
import ListenPanel from "./components/ListenPanel";
import Spotify from './components/Spotify'; 


const leftClickAudio = new Audio("/LeftClickSound.mp3");
const rightClickAudio = new Audio("/RightClickKick.mp3");

leftClickAudio.preload = "auto";
rightClickAudio.preload = "auto";



// Google Font (Orbitron)
const orbitronFontLink = "https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700&display=swap";

const images = [
  "/bg1.jpg", "/bg2.jpg", "/bg3.jpg", "/bg4.jpg", "/bg5.jpg", "/bg6.jpg", "/bg7.jpg", "/bg8.jpg",
  "/bg9.jpg", "/bg10.jpg", "/bg11.jpg", "/bg12.jpg", "/bg13.jpg"
];
const fadeTime = 28;
const tracks = [
  "/justrelax.mp3",
  "/DrumSircle-TheJourney.mp3",
  "/music3.mp3",
  "/DrumSircle-WelcomeToTheDrumSircle.mp3"
];

const buttonStyle = {
  border: "none",
  borderRadius: "50%",
  width: 53,
  height: 53,
  background: "rgba(0,0,0,0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  outline: "none",
  cursor: "pointer",
  transition: "box-shadow 0.5s",
  fontFamily: "'Orbitron', 'Poppins', 'Segoe UI', Arial, sans-serif"
};

const arrowButtonStyle = {
  ...buttonStyle,
  width: 22,
  height: 22,
  fontSize: 14,
  margin: 0,
  minWidth: 0,
  minHeight: 0
};

const gradientTextStyle = {
  background: "linear-gradient(90deg, #e0e0e0 0%, #fff 55%, #ededed 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontFamily: "'Orbitron', 'Poppins', 'Segoe UI', Arial, sans-serif",
  fontWeight: 600,
  letterSpacing: "0.02em",
  fontSize: 27,
  userSelect: "none",
  textAlign: "center"
};
const gradientTextArrow = {
  ...gradientTextStyle,
  fontSize: 13
};

const linkStyle = {
  color: "#00ffe0",
  fontSize: 18,
  fontWeight: 700,
  textDecoration: "none",
  fontFamily: "'Orbitron', 'Segoe UI', Arial, sans-serif",
  filter: "drop-shadow(0 0 4px #00ffe0aa)"
};

const seoByPath = {
  "/": {
    title: "Drum Sircle | Psychedelic EDM Artist Official Site",
    description:
      "Official Drum Sircle site. Explore music, videos, Spotify, and direct contact for bookings.",
  },
  "/videos": {
    title: "Drum Sircle Videos | Official Visuals",
    description:
      "Watch official Drum Sircle videos and visual performances.",
  },
  "/music": {
    title: "Drum Sircle Music | Tracks and Releases",
    description:
      "Listen to Drum Sircle tracks and explore latest electronic and psychedelic releases.",
  },
  "/contact": {
    title: "Contact Drum Sircle | Bookings and Inquiries",
    description:
      "Get in touch with Drum Sircle for bookings, collaborations, and event inquiries.",
  },
  "/spotify": {
    title: "Drum Sircle on Spotify | Stream Official Tracks",
    description:
      "Stream Drum Sircle on Spotify and follow for new electronic music releases.",
  },
};

function RouteSeo() {
  const location = useLocation();
  const path = location.pathname || "/";
  const routeSeo = seoByPath[path] || seoByPath["/"];
  const canonicalUrl = `https://www.drumsircle.com${path === "/" ? "/" : path}`;

  return (
    <Helmet>
      <title>{routeSeo.title}</title>
      <meta name="description" content={routeSeo.description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={routeSeo.title} />
      <meta property="og:description" content={routeSeo.description} />
      <meta name="twitter:title" content={routeSeo.title} />
      <meta name="twitter:description" content={routeSeo.description} />
    </Helmet>
  );
}


export default function App() {
  // --- STATE ---
  const [curr, setCurr] = useState(0);
  const [prev, setPrev] = useState(null);
  const currRef = useRef(0);
  const intervalRef = useRef(null);
  const fadeClearRef = useRef(null);

  // Nav/audio logic
  const [showHeader, setShowHeader] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Contact modal
  const [showContact, setShowContact] = useState(false);

  // Audio track index
  const [trackIdx, setTrackIdx] = useState(0);

  // For double-click prev logic
  const [lastPrevClick, setLastPrevClick] = useState(0);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [forcePlay, setForcePlay] = useState(false);

  useEffect(() => {
    currRef.current = curr;
  }, [curr]);

  // Preload so the incoming image is ready under the outgoing fade layer
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // --- BACKGROUND FADE LOGIC ---
  // Keep outgoing image covering the new one for the full fade (no lone first-frame flash).
  // Loop timing stays fadeTime between advances.
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const from = currRef.current;
      const to = (from + 1) % images.length;
      // Set prev first conceptually: both update in one React batch so the
      // outgoing layer mounts already covering the new current image.
      setPrev(from);
      setCurr(to);
      currRef.current = to;

      if (fadeClearRef.current) clearTimeout(fadeClearRef.current);
      fadeClearRef.current = setTimeout(() => {
        setPrev(null);
      }, fadeTime * 1000);
    }, fadeTime * 1000);

    return () => {
      clearInterval(intervalRef.current);
      if (fadeClearRef.current) clearTimeout(fadeClearRef.current);
    };
  }, []);

  // --- NAV LOGIC ---
  const handleHideHeader = (e) => {
    e?.stopPropagation?.();
    setShowHeader(false);
    setShowContact(false);
    setIsFullscreen(false); // Always exit full screen if hiding nav
  };
  const handleShowHeader = (e) => {
    e?.stopPropagation?.();
    setShowHeader(true);
  };
  const handlePlay = (e) => {
  e?.stopPropagation?.();
  setAudioPlaying((prev) => !prev);
};

const handleNext = (e) => {
  e?.stopPropagation?.();
  setTrackIdx(i => (i + 1) % tracks.length);
};

const handlePrev = (e) => {
  e?.stopPropagation?.();
  setTrackIdx(i => (i - 1 + tracks.length) % tracks.length);
};


  // --- OVERLAY FOR NAV ---
  const overlayClick = (e) => {
    if (isFullscreen) {
      // Exit full screen and bring nav/buttons back
      exitFullScreen();
      setIsFullscreen(false);
      setShowHeader(true);
      return;
    }
    if (!showHeader) handleShowHeader(e);
  };

  // --- FULLSCREEN API LOGIC ---
  const enterFullScreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
    setIsFullscreen(true);
    setShowHeader(false);
    setShowContact(false);
  };

  const exitFullScreen = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    else if (document.msExitFullscreen) document.msExitFullscreen();
    setIsFullscreen(false);
  };

  // Exit full screen on ESC or browser action
  useEffect(() => {
    function handleFSChange() {
      if (
        !document.fullscreenElement &&
        !document.mozFullScreenElement &&
        !document.webkitFullscreenElement &&
        !document.msFullscreenElement
      ) {
        setIsFullscreen(false);
      }
    }
    document.addEventListener("fullscreenchange", handleFSChange);
    document.addEventListener("webkitfullscreenchange", handleFSChange);
    document.addEventListener("mozfullscreenchange", handleFSChange);
    document.addEventListener("MSFullscreenChange", handleFSChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFSChange);
      document.removeEventListener("webkitfullscreenchange", handleFSChange);
      document.removeEventListener("mozfullscreenchange", handleFSChange);
      document.removeEventListener("MSFullscreenChange", handleFSChange);
    };
  }, []);

  useEffect(() => {
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  if (isMobile) return; // 🔇 Disable all click sounds on mobile

  const rightClickAudio = new Audio("/RightClickKick.mp3");

  const handleRightClick = (e) => {
    e.preventDefault(); // disables context menu
    const volume = Math.random() * (0.4005 - 0.2805) + 0.2805; // ~50%–57.8%
    rightClickAudio.volume = volume;
    rightClickAudio.currentTime = 0;
    rightClickAudio.play().catch(() => {});
  };

  window.addEventListener("contextmenu", handleRightClick);

  return () => {
    window.removeEventListener("contextmenu", handleRightClick);
  };
}, []);


  useEffect(() => {
  const disableRightClickMenu = (e) => {
    e.preventDefault();
  };

  window.addEventListener("contextmenu", disableRightClickMenu);

  return () => {
    window.removeEventListener("contextmenu", disableRightClickMenu);
  };
}, []);

  return (
    <Router>
      <RouteSeo />
      {/* Orbitron Font */}
      <link rel="stylesheet" href={orbitronFontLink} />

      <div
        style={{
          width: "100vw", height: "100vh",
          position: "fixed", top: 0, left: 0, overflow: "hidden"
        }}
        onClick={overlayClick}
      >
        {/* SEO-friendly H1 for main page */}
        <h1 style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          zIndex: -1
        }}>
          Drum Sircle - Psychedelic Sounds from Planet Earth
        </h1>
        {/* Keep links crawlable for bots without changing visible UI */}
        <nav
          aria-label="Site navigation"
          style={{
            position: "absolute",
            left: "-9999px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        >
          <Link to="/">Home</Link>
          <Link to="/videos">Videos</Link>
          <Link to="/music">Music</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/spotify">Spotify</Link>
        </nav>
        
        {/* Base black layer (just in case) */}
        <div style={{
          background: "#000",
          position: "absolute",
          top: 0, left: 0,
          width: "100vw", height: "100vh",
          zIndex: 0
        }} />
        {/* Current image underneath (revealed as previous fades out) */}
        <div
          style={{
            background: `url(${images[curr]}) center center / cover no-repeat`,
            width: "100vw", height: "100vh",
            position: "absolute", top: 0, left: 0, zIndex: 1
          }}
        />
        {/* Previous image on top — same paint as curr swap, starts at full opacity */}
        {prev !== null && (
          <div
            key={images[prev] + "-fade"}
            style={{
              background: `url(${images[prev]}) center center / cover no-repeat`,
              width: "100vw", height: "100vh",
              position: "absolute", top: 0, left: 0, zIndex: 2,
              opacity: 1,
              animation: `bgFadeOut ${fadeTime}s linear forwards`,
              willChange: "opacity",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Nav/header bar */}
        {showHeader && !isFullscreen && (
          <>
            

            {/* Play button - bottom left */}
<button
  onClick={handlePlay}
  style={{
    ...buttonStyle,
    position: "fixed",
    bottom: 12,
    left: 12,
    zIndex: 120,
    borderRadius: 20,
    fontSize: 27,
    animation: audioPlaying ? "glow 16s ease-in-out infinite" : "none",
    boxShadow: "0 0 17px #00ffe088"
  }}
  aria-label={audioPlaying ? "Pause" : "Play"}
>
  <span style={gradientTextStyle}>{audioPlaying ? "⏸" : "▶"}</span>
</button>

            {/* Fullscreen button - bottom right */}
            <button
              onClick={e => { e.stopPropagation(); enterFullScreen(); }}
              style={{
                ...buttonStyle,
                position: "fixed",
                bottom: 12,
                right: 12,
                zIndex: 120,
                borderRadius: 20,
                fontSize: 27,
                animation: "glow 16s ease-in-out infinite",
                boxShadow: "0 0 17px #00ffe088"
              }}
              aria-label="Full Screen"
            >
              {/* Unicode fullscreen icon: ⛶ (U+26F6) or SVG for better look */}
              <span style={gradientTextStyle}>⛶</span>
            </button>
          </>
        )}

        {/* Arrow buttons (top corners) */}
{showHeader && !isFullscreen && (
  <>
    {/* Prev - top left */}
    <button
      onClick={handlePrev}
      style={{
        ...arrowButtonStyle,
        width: 36,
        height: 36,
        fontSize: 20,
        position: "fixed",
        top: 18,
        left: 18,
        zIndex: 120,
        animation: audioPlaying ? "glow 16s ease-in-out infinite" : "none",
        opacity: audioPlaying ? 1 : 0.5
      }}
      aria-label="Previous song"
      tabIndex={audioPlaying ? 0 : -1}
      disabled={!audioPlaying}
    >
      <span style={gradientTextArrow}>◀</span>
    </button>

    {/* Next - top right */}
    <button
      onClick={handleNext}
      style={{
        ...arrowButtonStyle,
        width: 36,
        height: 36,
        fontSize: 20,
        position: "fixed",
        top: 18,
        right: 18,
        zIndex: 120,
        animation: audioPlaying ? "glow 16s ease-in-out infinite" : "none",
        opacity: audioPlaying ? 1 : 0.5
      }}
      aria-label="Next song"
      tabIndex={audioPlaying ? 0 : -1}
      disabled={!audioPlaying}
    >
      <span style={gradientTextArrow}>▶</span>
    </button>
  </>
)}


        {/* HUG-THE-WALL CONTACT TAB (ONLY ONE!) - middle right */}
{showHeader && !showContact && !isFullscreen && (
  <button
    onClick={e => { e.stopPropagation(); setShowContact(true); }}
    style={{
      position: "fixed",
      right: -95,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 110,
      width: 119,
      height: 36,
      background: "linear-gradient(90deg, rgba(0,255,224,0.75), rgba(255,46,232,0.75), rgba(0,255,224,0.75))",
      backgroundSize: "200% 200%",
      animation: "pulseGradient 32s ease-in-out infinite",
      color: "#fff",
      border: "none",
      borderRadius: "18px 0 0 18px",
      fontWeight: 700,
      fontSize: 18,
      letterSpacing: "0.03em",
      boxShadow: "0 0 14px #00ffe055",
      padding: "0 18px 0 12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      transition: "right 0.22s, background 0.22s, box-shadow 0.22s",
      opacity: 0.98,
      outline: "none"
    }}
    onMouseEnter={e => {
      e.currentTarget.style.right = "0";
      e.currentTarget.style.boxShadow = "0 0 22px #ff2ee8aa";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.right = "-95px";
      e.currentTarget.style.boxShadow = "0 0 14px #00ffe055";
    }}
    aria-label="Contact Drum Sircle"
  >
    <span style={{
      fontFamily: "'Orbitron', 'Segoe UI', Arial, sans-serif",
      fontWeight: 800,
      fontSize: 18,
      letterSpacing: ".08em",
      textShadow: "0 0 10px #0ff, 0 0 2px #000",
      opacity: 1,
      transition: "opacity 0.22s",
      whiteSpace: "nowrap",
      overflow: "hidden"
    }}>
      Contact
    </span>
  </button>
)}

{/* CONTACT FORM MODAL */}
{showContact === true && (
  <div
    onClick={() => setShowContact(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      zIndex: 9999, background: "rgba(0,0,0,0.74)",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: "relative",
        background: "rgba(17, 17, 17, 0.9)",
        borderRadius: 28,
        padding: 40,
        minWidth: 330,
        boxShadow: "0 0 44px #00ffe033"
      }}
    >
      <button
        onClick={() => setShowContact(false)}
        style={{
          position: "absolute", top: 18, right: 24,
          background: "none", border: "none",
          color: "#fff", fontSize: 29, cursor: "pointer"
        }}
        aria-label="Close"
      >✕</button>

      <ContactForm />

      {/* Socials Row (IG + YT) */}
      <div style={{
        marginTop: 28,
        display: "flex",
        justifyContent: "center",
        gap: 32
      }}>
        <a
          href="https://instagram.com/DrumSircle"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#00ffe0", fontWeight: 700, fontSize: 22, textDecoration: "none",
            filter: "drop-shadow(0 0 6px #00ffe0dd)"
          }}
          aria-label="Instagram"
        >IG</a>

        <a
          href="https://youtube.com/@DrumSircle"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#ff2ee8", fontWeight: 700, fontSize: 22, textDecoration: "none",
            filter: "drop-shadow(0 0 6px #ff2ee8cc)"
          }}
          aria-label="YouTube"
        >YouTube</a>
      </div>

      {/* Centered Spotify Icon */}
      <div style={{
        marginTop: 18,
        display: "flex",
        justifyContent: "center"
      }}>
        <a
          href="https://open.spotify.com/artist/24uo56EggPBjgfeyGExsMh"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          aria-label="Spotify"
        >
          <img
            src="/spotify-icon.png"
            alt="Spotify"
            style={{
              width: 38,
              height: 38,
              filter: "drop-shadow(0 0 7px #1fff91) brightness(1.3)",
              transition: "transform 0.3s"
            }}
            onMouseOver={e => e.currentTarget.style.transform = "scale(1.12)"}
            onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
          />
        </a>
      </div>

      <style>{`
        @keyframes pulseGlow {
          0% { text-shadow: 0 0 6px #00ffe088; }
          50% { text-shadow: 0 0 14px #ff2ee8aa; }
          100% { text-shadow: 0 0 6px #00ffe088; }
        }
      `}</style>

    </div>
  </div>
)}


{/* HUG-THE-WALL LISTEN TAB (LEFT SIDE, MIDDLE) */}
{showHeader && !showContact && !isFullscreen && (
  <button
    onClick={e => {
      e.stopPropagation();
      setShowContact("listen");
    }}
    style={{
      position: "fixed",
      left: -95,
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 110,
      width: 119,
      height: 36,
      background: "linear-gradient(270deg, rgba(0,255,224,0.75), rgba(255,46,232,0.75), rgba(0,255,224,0.75))",

      backgroundSize: "200% 200%",
      animation: "pulseGradient 32s ease-in-out infinite",
      color: "#fff",
      border: "none",
      borderRadius: "0 18px 18px 0",
      fontWeight: 700,
      fontSize: 18,
      letterSpacing: "0.03em",
      boxShadow: "0 0 14px #00ffe055",
      padding: "0 18px 0 12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      transition: "left 0.22s, background 0.22s, box-shadow 0.22s",
      opacity: 0.98,
      outline: "none"
    }}
    onMouseEnter={e => {
      e.currentTarget.style.left = "0";
      e.currentTarget.style.boxShadow = "0 0 22px #ff2ee8aa";
    }}
    onMouseLeave={e => {
      e.currentTarget.style.left = "-95px";
      e.currentTarget.style.boxShadow = "0 0 14px #00ffe055";
    }}
    aria-label="Listen to Drum Sircle"
  >
    <span style={{
      fontFamily: "'Orbitron', 'Segoe UI', Arial, sans-serif",
      fontWeight: 800,
      fontSize: 18,
      letterSpacing: ".08em",
      textShadow: "0 0 10px #0ff, 0 0 2px #000",
      opacity: 1,
      transition: "opacity 0.22s",
      whiteSpace: "nowrap",
      overflow: "hidden"
    }}>
      Listen
    </span>
  </button>
)}

{/* LISTEN MODAL */}
{showContact === "listen" && (
  <div
    onClick={() => setShowContact(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      zIndex: 9999, background: "rgba(0,0,0,0.74)",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: "relative",
        background: "rgba(17, 17, 17, 0.9)",
        borderRadius: 28,
        padding: 40,
        minWidth: 330,
        boxShadow: "0 0 44px #00ffe033"
      }}
    >
      <button
        onClick={() => setShowContact(false)}
        style={{
          position: "absolute", top: 18, right: 24,
          background: "none", border: "none",
          color: "#fff", fontSize: 29, cursor: "pointer"
        }}
        aria-label="Close"
      >✕</button>
      <h2 style={{
        marginTop: 0,
        marginBottom: 16,
        fontSize: 20,
        fontFamily: "'Orbitron', 'Segoe UI', Arial, sans-serif",
        color: "#00ffe0",
        textAlign: "center"
      }}>
        Listen to Drum Sircle
      </h2>
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        alignItems: "center",
        marginBottom: 8
      }}>
                 <Link to="/spotify" style={linkStyle} onClick={() => setShowContact(false)}>
   Spotify
 </Link>

        <a href="https://music.apple.com/us/artist/drum-sircle/1821977642" target="_blank" rel="noopener noreferrer" style={linkStyle}>Apple Music</a>
        <a href="https://youtube.com/@DrumSircle" target="_blank" rel="noopener noreferrer" style={linkStyle}>YouTube</a>
        <Link to="/" style={linkStyle} onClick={() => setShowContact(false)}>Home</Link>
      </div>
    </div>
  </div>
)}



        <AudioPlayer
  playing={audioPlaying || forcePlay}
  tracks={tracks}
  trackIdx={trackIdx}
  setTrackIdx={setTrackIdx}
  onEnded={() => setForcePlay(false)} // Optional: Reset after it's playing
/>



        {/* Routing for pages */}
        <div style={{ position: "relative", zIndex: 3, height: "100vh" }}>
          <Routes>
            <Route path="/videos" element={<Videos />} />
            <Route path="/music" element={<Music />} />
            <Route path="/contact" element={<ContactForm />} />
            <Route path="/spotify" element={<Spotify />} />
          </Routes>
        </div>

        {/* Animated glow keyframes */}
<style>{`
  @keyframes bgFadeOut {
      from { opacity: 1; }
      to   { opacity: 0; }
    }

  @keyframes glow {
      0%   { box-shadow: 0 0 10px 4px #00ffe0cc; }
      50%  { box-shadow: 0 0 17px 8px #ff2ee8cc; }
      100% { box-shadow: 0 0 10px 4px #00ffe0cc; }
    }

    @keyframes pulseGradient {
      0%   { background-position: 0% 50%; }
      25%  { background-position: 50% 60%; }
      50%  { background-position: 100% 50%; }
      75%  { background-position: 50% 40%; }
      100% { background-position: 0% 50%; }
    }

    /* Mobile fix: soften modal box background */
    @media screen and (max-width: 768px) {
      .mobile-modal-fix {
        background: rgba(17, 17, 17, 0.88) !important;
      }

  /* ✅ Mobile font fallback for button icons */
  @media screen and (max-width: 768px) {
    button, button span {
      font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif !important;
      font-weight: 800;
    }
  }
  
  /* iPad-specific fixes for Unicode symbols */
  @media screen and (max-width: 1024px) {
    button, button span {
      font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif !important;
      font-weight: 800;
    }
  }
`}</style>
</div>
</Router>
);
}
